<!-- eslint-disable max-len -->
<template>
    <div id="study__container" class="study__container">
        <AssignmentsSidePanel
            v-if="showAssignmentsSidePanel && assignments"
            key="head"
            :assignments="assignments"
            :current-exam="currentExam"
            @close="setShowAssignmentsSidePanelFalse"
        />
        <div v-dark class="study__line" />
        <div
            v-dark
            class="study"
            :class="{
                [`study--${timeOfDay}`]: true,
            }"
        >
            <TrialLevelUpModal
                v-if="!isLoading && showTrialLevelUpModal && !isShowingUpgradeModal && !isSSORoute"
                @close="setShowTrialLevelUpModalFalse"
            />
            <WelcomeSeriesModal
                v-else-if="!isLoading && showWelcomeSeriesModal && !isSSORoute && !isShowingUpgradeModal"
                @close="setShowWelcomeSeriesModalFalse"
            />
            <QuizSettingsSidePanel v-if="showQuizSettingsSidePanel" @close="setShowQuizSettingsSidePanelFalse" />
            <QuizModeSetupModal
                v-if="quizMode"
                :mode="quizMode"
                @start="startQuiz"
                @close="setQuizModeNull"
            />
            <UnfinishedQuizModal
                v-if="showUnfinishedQuizModal && unfinishedMode && clickedMode"
                :unfinished-mode="unfinishedMode"
                :clicked-mode="clickedMode"
                @close="setShowUnfinishedQuizModalFalse"
                @startNew="beginQuizMode"
                @resume="resumeMode"
            />
            <ExamDateModal v-if="isShowingExamDateModal" @close="closeExamDateModal" />
            <div class="study__header">
                <div
                    v-dark
                    class="study__header-left"
                    :class="{ [`study__header-left--${timeOfDay}`]: true }"
                >
                    <Blob class="study__blob" />
                    <div
                        v-dark
                        class="study__title"
                        :class="{
                            'study__title--ten-day-cta': !isLoading && showTenDayCta,
                        }"
                    >
                        <h1>
                            <template v-if="timeOfDay === 'early'">
                                Rise 'n Shine
                            </template>
                            <template v-else-if="timeOfDay === 'morning'">
                                Good Morning
                            </template>
                            <template v-else-if="timeOfDay === 'afternoon'">
                                Good Afternoon
                            </template>
                            <template v-else-if="timeOfDay === 'evening'">
                                Good Evening
                            </template>
                            <template v-else>
                                Hey, Night Owl
                            </template>
                        </h1>
                        <div class="study__subhead">
                            <div
                                v-if="!isLoading && isTomorrowExamDay"
                                class="study__greeting"
                                v-html="tomorrowExamDayGreeting"
                            />
                            <div
                                v-else-if="!isLoading && showTenDayCta"
                                class="study__share-cta"
                            >
                                <div v-dark class="study__share-cta-message">
                                    You have a 10 day streak! Share the love.
                                    <PocketButton
                                        class="study__share-cta-btn"
                                        type="tertiary"
                                        :is-dark-mode="isDarkMode"
                                        @click="goToSharePage"
                                    >
                                        Give 20% Off
                                    </PocketButton>
                                </div>
                            </div>
                            <div
                                v-else-if="!isLoading && quizBasedGreeting"
                                class="study__greeting"
                                v-html="quizBasedGreeting"
                            />
                            <div
                                v-else-if="!isLoading && canShowExamDateGreeting"
                                class="study__exam-date-greeting"
                            >
                                <div v-dark class="study__exam-date-greeting-message">
                                    When's the big day? Add your
                                    <PocketButton
                                        class="study__exam-date-greeting-btn"
                                        type="tertiary"
                                        :is-dark-mode="isDarkMode"
                                        @click="setShowingExamDateModalTrue"
                                    >
                                        exam date
                                    </PocketButton>.
                                </div>
                            </div>
                            <div
                                v-else-if="!isLoading"
                                v-dark="isDarkMode || isMidnight"
                                class="study__greeting"
                                v-html="genericSubhead"
                            />
                        </div>
                    </div>
                    <CalendarContainer />
                </div>
                <div v-dark="isDarkMode || isMidnight" class="study__header-right">
                    <QotDContainer :key="showWelcomeSeriesModal ? 'welcome-series-modal' : 'qotd-container'" />
                </div>
            </div>
            <div
                class="study__body"
                :class="{
                    'study__body--assignments': canSeeAssignmentMode,
                }"
            >
                <ExamDateBanner
                    v-if="canSeeExamDateBanner"
                    :is-dark-mode="isDarkMode"
                    class="study__exam-date-banner"
                    :class="{
                        'study__exam-date-banner--with-assignment': canSeeAssignmentMode,
                    }"
                    @close="closeExamDateBanner"
                />
                <PocketButton
                    v-if="!isLoading && breakpoint === 'black-bear' && !hasPremiumAccess"
                    :is-dark-mode="isDarkMode"
                    class="study__mobile-cta"
                    :is-loading="isLoading"
                    @click="openUpgradeModal"
                >
                    Upgrade for all quiz modes
                </PocketButton>
                <div
                    v-if="canSeeAssignmentMode"
                    class="study__assignment-mode"
                >
                    <Mode
                        class="study__mode"
                        name="Assignments"
                        :number-of-assignments="numberOfAssignments"
                        @click="openAssignmentSidePanel"
                    />
                </div>
                <div class="study__body-top">
                    <h2 v-dark class="study__body-title">
                        Quiz Modes
                    </h2>
                    <PocketButton
                        :is-dark-mode="isDarkMode"
                        type="tertiary-small"
                        @click="setShowQuizSettingsSidePanelTrue"
                    >
                        Quiz Settings
                    </PocketButton>
                </div>
                <div class="study__modes">
                    <Mode
                        class="study__mode study__mode-qotd"
                        name="Question of the Day"
                        @click="clickQuizMode('qotd')"
                    />
                    <Mode
                        class="study__mode"
                        name="Quick 10"
                        @click="clickQuizMode('quick10')"
                    />
                    <Mode
                        class="study__mode"
                        name="Timed Quiz"
                        @click="clickQuizMode('timed')"
                    />
                    <Mode
                        v-if="showLevelUpMode"
                        class="study__mode"
                        name="Level Up"
                        :is-premium="true"
                        :show-level-up-free-trial-tag="canTrialLevelUp"
                        @click="clickQuizMode('levelUp')"
                    />
                    <Mode
                        class="study__mode"
                        name="Weakest Subject"
                        :is-premium="true"
                        @click="clickQuizMode('weakest')"
                    />
                    <Mode
                        class="study__mode"
                        name="Missed Questions"
                        :is-premium="true"
                        @click="clickQuizMode('missed')"
                    />
                    <Mode
                        class="study__mode"
                        name="Build Your Own"
                        :is-premium="true"
                        @click="clickQuizMode('custom')"
                    />
                    <Mode
                        v-if="areMockExamsAvailable"
                        class="study__mode"
                        name="Mock Exam"
                        :is-premium="true"
                        @click="clickQuizMode('mockExam')"
                    />
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import WelcomeSeriesModal from '@/components/WelcomeSeriesModal.vue'
import { userModule } from '@/store/user/module'
import CalendarContainer from '@/components/Study/CalendarContainer.vue'
import QuizSettingsSidePanel from '@/components/Study/QuizSettingsSidePanel.vue'
import QotDContainer from '@/components/Study/QotDContainer.vue'
import * as utils from '@/utils'
import Blob from '@/components/Study/Blob.vue'
import UIKit from '@pocketprep/ui-kit'
import Mode from '@/components/Study/Mode.vue'
import QuizModeSetupModal from '@/components/Study/QuizModeSetupModal.vue'
import UnfinishedQuizModal from '@/components/Study/UnfinishedQuizModal.vue'
import AssignmentsSidePanel from '@/components/Assignments/AssignmentsSidePanel.vue'
import ExamDateModal from '@/components/ExamDateModal.vue'
import ExamDateBanner from '@/components/ExamDateBanner.vue'
import { questionModule } from '@/store/question/module'
import { quizModule } from '@/store/quiz/module'
import type { TQuizMode } from '@/store/quiz/state'
import type { TCreateActiveQuizParams } from '@/store/quiz/actions'
import { mockExamModule } from '@/store/mockExam/module'
import { analyticsModule } from '@/store/analytics/module'
import { screenModule } from '@/store/screen/module'
import { userExamMetadataModule } from '@/store/userExamMetadata/module'
import { bundleModule } from '@/store/bundle/module'
import { examMetadataModule } from '@/store/examMetadata/module'
import { subscriptionModule } from '@/store/subscription/module'
import { referralModule } from '@/store/referral/module'
import { assignmentModule } from '@/store/assignment/module'
import { studentModule } from '@/store/student/module'
import type { Study as StudyTypes, Teach } from '@pocketprep/types'
import { keywordsModule } from '@/store/keywords/module'
import { upgradeModule } from '@/store/upgrade/module'
import TrialLevelUpModal from '@/components/TrialLevelUpModal.vue'
import { mobileModule } from '@/store/mobile/module'

const {
    Button: PocketButton,
} = UIKit

const route = useRoute()
const router = useRouter()

const showWelcomeSeriesModal = ref(false)
const showUnfinishedQuizModal = ref(false)
const unfinishedMode = ref<TQuizMode | null>(null)
const clickedMode = ref<TQuizMode | null>(null)
const timeOfDay = utils.timeOfDay()
const showQuizSettingsSidePanel = ref(false)
const quizMode = ref<null | 'weakest' | 'missed' | 'timed'>(null)
const isLoading = ref(true)
const showAssignmentsSidePanel = ref(false)
const showLevelUpMode = ref(false)
const isShowingExamDateModal = ref(false)
const clientVersion = import.meta.env.VUE_APP_VERSION
const showTrialLevelUpModal = ref(false)
const examDateGreetingChance = ref(Math.random())
const genericSubhead = ref('')

const isSSORoute = computed(() => {
    return typeof route.query.ssoOrgName === 'string'
})

const isDarkMode = computed(() => {
    return userModule.state.settings.isDarkMode
})

const breakpoint = computed(() => {
    return screenModule.getters.getBreakpoint()
})

const userExamMetadata = computed(() => {
    return userExamMetadataModule.getters.getCurrentUserExamMetadata()
})

const isMidnight = computed(() => {
    return utils.timeOfDay() === 'midnight'
})

const currentBundle = computed(() => {
    return bundleModule.getters.getCurrentBundle()
})

const currentExam = computed(() => {
    return examMetadataModule.getters.getCurrentExamMetadata()
})


const numOfKeywordsInCurrentExam = computed(() => {
    return keywordsModule.getters.getKeywordDefinitions().size
})

const areMockExamsAvailable = computed(() => {
    return mockExamModule.getters.getCurrentMockExams().length > 0
})

const isTomorrowExamDay = computed(() => {
    if (userExamMetadata.value?.scheduledExamDate) {
        const examDay = new Date(userExamMetadata.value.scheduledExamDate.iso).getTime()
        const tomorrow = new Date()
        tomorrow.setHours(24, 0, 0, 0)
        return tomorrow.getTime() === examDay
    }
    return false
})

const tomorrowExamDayGreeting = computed(() => {
    if (isTomorrowExamDay.value) {
        const tomorrowExamSubheads = [
            'Bonne chance! (French for kick butt on your exam tomorrow!) <img src="img/study/france.png" alt="">',
            'Tomorrow\'s the day. Keep calm and exam on! ',
            'Big day tomorrow. Wishing you tons of luck! <img src="img/study/lucky.png" alt="">',
            'Hey <img src="img/study/wave.png" alt="">. We\'ll be rooting for you tomorrow. ',
        ]

        const randomIndex = Math.round(Math.random() * (tomorrowExamSubheads.length - 1))
        return tomorrowExamSubheads[randomIndex]
    }
    return false
})

const quizBasedGreeting = computed(() => {
    if (quizModule.getters.getQuizzes().length) {
        const quizBasedSubheads = [
            { quizNumber: 10, subHead: '10 quizzes completed! Amazing!' },
            {
                quizNumber: 20,
                // eslint-disable-next-line max-len
                subHead: 'You’ve slayed 20 quizzes so far. Keep it up! <img src="img/study/axe.png" alt=""><img src="img/study/vampire.png" alt="">',
            },
            {
                quizNumber: 30,
                subHead: '30 Quizzes down! However many to go <img src="img/study/wink.png" alt="">.',
            },
            { quizNumber: 40, subHead: '40 Quizzes done - that\'s a lot of thinking. ' },
            { quizNumber: 50, subHead: '50 Quizzes finished. Most excellent.' },
            { quizNumber: 60, subHead: '60 Quizzes down - we\'re impressed!' },
            {
                quizNumber: 75,
                subHead: '75 Quizzes completed. Baller status achieved. <img src="img/study/mic.png" alt="">',
            },
            {
                quizNumber: 100,
                subHead: 'You defeated 100 quizzes! All hail the conquerer. <img src="img/study/crown.png" alt="">',
            },
        ]

        const quizNumberIndex = quizBasedSubheads.findIndex(q => {
            return q.quizNumber === quizModule.getters.getQuizzes().length
        })
        if (quizNumberIndex > -1) {
            return quizBasedSubheads[quizNumberIndex].subHead
        }
        return false
    }
    return false
})

/**
 * Should only see the exam date greeting if:
 * - exam date banner is not visible
 * - no exam date already set
 * - user was created >72 hours ago
 *
 * We also randomize the chance of this greeting being shown, so the user still sees other time of day greetings
 */
const canShowExamDateGreeting = computed(() => {
    if (!user.value) {
        return false
    }

    // Check if exam date banner is visible
    const canSeeExamDateBannerVal = userExamMetadataModule.getters.getCanSeeExamDateBanner('Study')
    if (canSeeExamDateBannerVal) {
        return false
    }

    // Check if user has already scheduled their exam date
    const hasScheduledExamDate = !!userExamMetadataModule.getters.getScheduledExamDate()
    if (hasScheduledExamDate) {
        return false
    }

    // Check if user was created >72 hours ago
    const userCreatedAt = new Date(user.value.createdAt)
    const seventyTwoHoursAgo = Date.now() - (72 * 60 * 60 * 1000)
    const isPast72Hours = userCreatedAt.getTime() <= seventyTwoHoursAgo
    if (!isPast72Hours) {
        return false
    }

    // We still want the user to sometimes see the time of day greetings
    return examDateGreetingChance.value <= 0.05
})

const user = computed(() => {
    return userModule.state.user
})

const hasPremiumAccess = computed(() => {
    return subscriptionModule.getters.getSubscriptionForExamId(currentExam.value?.objectId)
})

const tenDayStreakConfig = computed(() => {
    return userExamMetadataModule.getters.getMessageReferralConfig()?.tenDayStreak
})

const streakCount = computed(() => {
    return quizModule.getters.getStreakCount({ filterQuizMode: false })
})

const isVisibleStartingToday = computed(() => {
    if (tenDayStreakConfig.value?.visibleStartingAt?.iso) {
        const today = utils.formatDate(new Date(), 'month', 0)
        const startingAt = new Date(tenDayStreakConfig.value.visibleStartingAt.iso)
        const formattedVisibleDate = utils.formatDate(startingAt, 'month', 0)
        if (today === formattedVisibleDate) {
            return true
        }
    }
    return false
})

const wasVisibleYesterday = computed(() => {
    const date = new Date()
    date.setDate(date.getDate() - 1)
    const yesterday = utils.formatDate(date, 'month', 0)
    if (tenDayStreakConfig.value?.visibleStartingAt?.iso) {
        const startingAt = new Date(tenDayStreakConfig.value.visibleStartingAt.iso)
        const formattedDate = utils.formatDate(startingAt, 'month', 0)
        if (yesterday === formattedDate) {
            return true
        }
    }
    return false
})

const showTenDayCta = computed(() => {
    // Do not show if tomorrow is the user's exam date
    if (isTomorrowExamDay.value) {
        return false
    }

    // Only show to subscribed premium users
    if (typeof hasPremiumAccess.value !== 'object') {
        return false
    }

    // Show if a user has a streak of ten or more days
    if (streakCount.value < 10) {
        return false
    }

    // Premium user with a 10 day streak but no config
    if (!tenDayStreakConfig.value) {
        return true
    }

    // Show once calendar month
    if (!isVisibleStartingToday.value) {
        return false
    }

    // Do not show if the CTA was shown yesterday
    if (wasVisibleYesterday.value) {
        return false
    }

    // Has a config and it should be shown today
    return true
})

const currentSubscriptionPlanLength = computed(() => {
    return subscriptionModule.getters.getSubscriptionPlanLengthByExamId(currentExam.value?.objectId)
})

const currentUserOnboardingCompleted = computed(() => {
    return user.value?.onboardingCompleted
})

const areAssignmentsEnabled = computed(() => {
    // assignments are enabled when an org has created at least one assignment
    return assignmentModule.getters.getAreAssignmentsEnabled()
})

const canSeeAssignmentMode = computed(() => {
    return !isLoading.value
        && (breakpoint.value === 'brown-bear' || breakpoint.value === 'black-bear')
        && hasPremiumAccess.value
        && areAssignmentsEnabled.value
})

const assignments = computed(() => {
    return assignmentModule.getters.getSortedAvailableAssignments()
})

const numberOfAssignments = computed(() => {
    return assignments.value.length
})

const studentOrgs = computed(() => {
    return studentModule.getters.getStudentOrgs()
})

const isShowingUpgradeModal = computed(() => {
    return upgradeModule.getters.getIsShowingUpgradeModal()
})

const canSeeExamDateBanner = computed(() => {
    return userExamMetadataModule.getters.getCanSeeExamDateBanner('Study')
})

const isOOAP = computed(() => {
    return mobileModule.getters.isInArrivedFromMobileState()
})

const canTrialLevelUp = computed(() => {
    return userExamMetadataModule.getters.getCanTrialLevelUp()
})

const crossPlatformConfig = computed(() => {
    return userModule.getters.getCrossPlatformConfig()
})

const canShowTrialLevelUpModal = computed(() => {
    if (
        crossPlatformConfig.value?.levelUpFreeTrial?.hasSeenLevelUpTrialSuggestion
        || userModule.state.user?.webConfig?.hasSeenWelcomeModal
        || userModule.state.user?.webConfig?.hasSeenOnboarding
    ) {
        return false
    }

    return canTrialLevelUp.value && showLevelUpMode.value
})

onMounted(async () => {
    isLoading.value = true

    await Promise.all([
        mockExamModule.actions.fetchMockExams(),
        questionModule.actions.fetchSerialQuestionInfoLib(),
        userExamMetadataModule.actions.fetchUserExamMetadata(),
        quizModule.actions.fetchAnsweredQuestions(),
        userModule.actions.fetchUserData(),
        referralModule.actions.validateReferral(),
        bundleModule.actions.fetchBundles(),
        examMetadataModule.actions.fetchExamMetadata(),
    ])
    await updateShowLevelUpMode()

    const studyContainerEl = document.getElementById('study__container')
    if (studyContainerEl && breakpoint.value !== 'black-bear') {
        studyContainerEl.scrollIntoView()
    }

    if (canShowTrialLevelUpModal.value) {
        showTrialLevelUpModal.value = true
    } else {
        showWelcomeSeriesModal.value =
            !userModule.state.user?.webConfig?.hasSeenWelcomeModal
            && !crossPlatformConfig.value?.levelUpFreeTrial?.hasSeenLevelUpTrialSuggestion
            && !userModule.state.user?.webConfig?.hasSeenOnboarding
    }

    // update ten date config if ten day cta was shown yesterday
    if (wasVisibleYesterday.value) {
        const nextMonth = new Date()
        nextMonth.setMonth(nextMonth.getMonth() + 1)
        await updateTenDayConfig(nextMonth)
    }

    if (showTenDayCta.value && !tenDayStreakConfig.value) {
        // update config if showTenDayCta and user does not have a config
        const today = new Date()
        await updateTenDayConfig(today)
    }

    if (
        (showWelcomeSeriesModal.value || showTrialLevelUpModal.value)
        && !currentUserOnboardingCompleted.value
        && currentExam.value?.examGuid
        && currentBundle.value?.objectId
        && currentSubscriptionPlanLength.value
        && !isOOAP.value
    ) {
        await userModule.actions.onboardingCompleted({
            firstExamGuid: currentExam.value.examGuid,
            firstBundleId: currentBundle.value.objectId,
            signedUpOn: 'Web',
            onboardingResult: currentSubscriptionPlanLength.value,
        })
    }

    initGenericSubhead()
    isLoading.value = false
})

const initGenericSubhead = () => {
    const genericSubheads = [
        'Greetings and happy studying.',
        'It\'s always a good time to study.',
        'It\'s an excellent day to study.',
        'It\'s a fine time to study.',
        'WOOT. Time for some studying.',
        'Nice to see you. Enjoy your studies.',
        'Hiya. Happy studying. <img src="img/study/wave.png" alt="">',
        'Remember to hydrate while you study. <img src="img/study/droplet.png" alt="">',
        'When in doubt, study trout! (Wait...that\'s not right.) <img src="img/study/fish.png" alt="">',
        'Kicking butt and taking names - keep up the good work.',
        'Hot tip: answering 100 questions a week improves your chances of passing!',
        'Welcome back! Ready when you are.',
        'One session closer to your goal. <img src="img/study/muscle.png" alt="">',
        'You came, you saw, you studied.',
        'To study or not to study? That\'s an easy question: study!',
        'What quiz will you take today?',
        'You\'re in your study era. We love to see it.',
    ]

    const timeSpecificSubheads = {
        'early': [
            'Hey - you\'re awake. Welcome to study central.',
            'Early bird gets the worm, er...coffee?',
            'You\'re up early. Happy studying. <img src="img/study/smile.png" alt="">',
            'Time for a stretch \'n study.',
            'Top \'o the mornin\' to ye! <img src="img/study/tophat.png" alt="">',
            'It\'s early. Sup. <img src="img/study/smile.png" alt="">',
            'Big morning study energy comin\' at ya!',
            'Behold! The morning! Go forth and study!',
            'Starting the day strong. #success',
            'We\'re happy you\'re here!',
            'Every morning is a new beginning.',
            'What can you learn today?',
            'Hey you. You\'re finally awake.',
            'Pre-dawn dedication? Respect. <img src="img/study/salute.png" alt="">',
            'The world is asleep. You\'re not. Let\'s go.',
        ],
        'morning': [
            'It\'s a great morning to study. <img src="img/study/sun.png" alt="">',
            'Your journey to awesomeness begins.',
            'Looks like you\'re getting a studious start to your day.',
            'Get that caffeine ready - it\'s study time. <img src="img/study/coffee.png" alt="">',
            'Welcome to your latest study sesh!',
            'Study now, play later. Your evening will thank you.',
            'Fact: the earth is round. Also fact: you\'ve got this.',
            'It\'s early, but not too early. Neato.',
            'Got that studying feeling?',
            'Carpe diem, but like, with studying.',
            'Rise. Shine. Study. Repeat.',
            'Morning brain is peak brain. Let\'s use it.',
            'Today just got more productive.',
            'Another day, another chance to crush it.',
        ],
        'afternoon': [
            'Time for tea or a quick study sesh? <img src="img/study/tea.png" alt="">',
            'No slump, just studying.',
            'Study on, Wayne. Study on, Garth.',
            'Fuel your study fire. <img src="img/study/taco.png" alt="">',
            'Way to keep your <img src="img/study/brain.png" alt="brain"> firing on all cylinders.',
            'We\'re mightly pleased you\'re here!',
            'Humans share 70% of our DNA with slugs.',
            'You showed up. That\'s half the battle.',
            'Half the day left. You\'re making it count.',
            'Look alive! Study time is now.',
            'Honey never expires. Neither does knowledge.',
            'Keep on keeping on.',
        ],
        'evening': [
            'The word ‘evening\' comes from an Old English word æfnung. Study on.',
            'Hey! It\'s kinda dark.',
            'Way to wrap up your day with a quick round of studying.',
            'Way to go on the evening study sesh. <img src="img/study/moon.png" alt="">',
            'EOD = BOS (beginning of studying)',
            'How\'s about dinner with a side of study?',
            'The sun is 10,000 degrees F. So is this study sesh. <img src="img/study/fire.png" alt="">',
            'Always hydrate before you study. <img src="img/study/droplet.png" alt="">',
            'Dance like no one\'s watching, study like your professor lives next door.',
            'Sunsets and studying pair together like PB&J.',
            'Golden hour, golden opportunity.',
            'Good news! Sleep after studying improves memory.',
            'You saved studying for dessert. Smart.',
            'Despite everything, you\'re here studying.',
            'Steady studying steadily strengthens students (try that three times fast).',
        ],
        'midnight': [
            'Farfegnugen. Said no one ever about studying. Whatever, it\'s late.',
            'There\'s midnight oil, and then there\'s espresso. Cheers. <img src="img/study/coffee.png" alt="">',
            'Maximum effort. Well done. <img src="img/study/highfive.png" alt="">',
            'Ermergersh, what time is it?',
            'Hi <img src="img/study/wave.png" alt="">. We see you! (In a non-creepy way)',
            'Time. This is no time to talk about time.',
            'Wee small hours of the morning: <img src="img/study/wave.png" alt="">',
            'Non Sequitur is a great word.',
            'Thou studiest most excellently.',
            'The best time to study is when you can!',
            'A+ for being awake right now. Seriously.',
            'Late night mode: engaged. <img src="img/study/nightCity.png" alt="">',
            'Late night crew, we see you.',
            'The midnight grind hits different.',
            'We would still love you if you were a worm. <img src="img/study/worm.png" alt="">',
        ],
    }

    if (numOfKeywordsInCurrentExam.value > 0) {
        genericSubheads.push(
            'Learning vocabulary can be tricky. Click highlighted words in explanations to see definitions.'
        )
    }

    const subheads = [ ...genericSubheads, ...(timeSpecificSubheads[timeOfDay] || []) ]
    const randomIndex = Math.round(Math.random() * (subheads.length - 1))
    genericSubhead.value = subheads[randomIndex]
}

const closeExamDateModal = () => {
    // If the user set their exam date, then show a temporary subhead message
    const daysUntilExam = userExamMetadataModule.getters.getDaysUntilExam()
    const subheadEl = document.querySelector('.study__subhead')
    if (subheadEl && daysUntilExam && daysUntilExam > 1) {
        subheadEl.innerHTML = `${daysUntilExam} days away. You've got this!`
    }
    isShowingExamDateModal.value = false
}

const closeExamDateBanner = () => {
    userModule.actions.markMessageSeen('examDateBanner')
}

const openUpgradeModal = () => {
    upgradeModule.actions.openUpgradeModal()
}

const clickQuizMode = async (mode: TQuizMode) => {
    if (mode === 'mockExam') {
        await analyticsModule.actions.trackEvent('Mock_Exam_Clicked')
    }

    const activeQuiz = quizModule.getters.getActiveQuiz()
    const activeMockExam = mockExamModule.getters.getActiveMockExamQuiz()
    const quizOrExamToResume = activeMockExam || activeQuiz
    const currentExamMatchesActiveQuiz = quizOrExamToResume?.examGuid === currentExam.value?.examGuid
    // make sure there is a quizOrExamToResume && currentExam examGuids match
    if (currentExamMatchesActiveQuiz && quizOrExamToResume) {
        // check to see if the user id on the quizOrExamToResume matches the current user id
        // as we roll this out there will be active quizzes/mock exams in localStorage that do not have userId
        // if userId does not exist on them allow the user to decide if they will close the UnfinishedQuizModal
        if (!quizOrExamToResume.userId || quizOrExamToResume.userId === user.value?.objectId) {
            clickedMode.value = mode
            unfinishedMode.value = quizOrExamToResume.mode
            showUnfinishedQuizModal.value = true
            return
        }
    }

    beginQuizMode(mode)
}

const beginQuizMode = (mode: TQuizMode) => {
    if (mode !== 'levelUp' && localStorage.getItem('tryLevelUpExperiment')) {
        localStorage.removeItem('tryLevelUpExperiment')
    }
    showUnfinishedQuizModal.value = false
    if (mode === 'qotd') {
        startQuiz({ mode: 'qotd' })
    } else if (mode === 'quick10') {
        startQuiz({ mode: 'quick10', questionCount: 10 })
    } else if (mode === 'timed') {
        quizMode.value = 'timed'
    } else if (mode === 'levelUp') {
        userModule.actions.incrementLevelUpOpenedCount()
        router.push({ name: 'level-up-intro' })
    } else if (mode === 'weakest') {
        quizMode.value = 'weakest'
    } else if (mode === 'missed') {
        quizMode.value = 'missed'
    } else if (mode === 'custom') {
        router.push({ name: 'byoq' })
    } else if (mode === 'mockExam') {
        router.push({ name: 'mock-exam-intro' })
    }
}

const resumeMode = (mode: TQuizMode) => {
    if (mode === 'mockExam') {
        router.push({ name: 'mock-exam' })
    } else {
        router.push({ name: 'quiz' })
    }
}

const startQuiz = async (quizInfo: TCreateActiveQuizParams | { mode: 'qotd' }) => {
    if (isLoading.value) {
        return
    }

    isLoading.value = true
    if (quizInfo.mode === 'qotd') {
        router.push({ name: 'qotd' })
        return
    } else {
        await quizModule.actions.createActiveQuiz(quizInfo)
    }
    isLoading.value = false

    router.push({ name: 'quiz' })
}

const updateTenDayConfig = async (date: Date) => {
    if (!userExamMetadata.value) {
        return
    }

    await userExamMetadataModule.actions.upsertUserExamMetadata({
        examGuid: userExamMetadata.value.examGuid,
        messageConfig: {
            ...(userExamMetadata.value.messageConfig || {}),
            referral: {
                ...(userExamMetadata.value.messageConfig?.referral as StudyTypes.Class.MessageConfigOptions || {}),
                tenDayStreak: {
                    visibleStartingAt: date,
                },
            },
        },
    })
}

const goToSharePage = () => {
    router.push({
        name: 'share',
        query: {
            utm_content: '10_day_streak',
        },
    })
}

const openAssignmentSidePanel = () => {
    showAssignmentsSidePanel.value = true
    let orgId = ''

    if (assignments.value.length) {
        const assignmentOrg = assignments.value[0].organization as Teach.Class.OrganizationJSON
        orgId = assignmentOrg?.objectId
    } else {
        orgId = studentOrgs.value[0]?.objectId
    }

    analyticsModule.actions.amplitudeTrack('Button Clicked', {
        client: 'Web Study',
        clientAppId: 'study.pocketprep.com',
        clientVersion: clientVersion,
        callToAction: 'Assignments',
        containerName: 'Quiz Modes',
        screenName: analyticsModule.getters.getScreenName(route),
        examGuid: currentExam.value?.examGuid || '',
        bundleId: currentBundle.value?.objectId || '',
        organizationId: orgId,
    })
}

const updateShowLevelUpMode = async () => {
    if (currentExam.value?.examGuid) {
        showLevelUpMode.value = await quizModule.actions.isLevelUpEnabled({
            examGuid: currentExam.value.examGuid,
        })
    }
}

watch(currentExam, () => {
    updateShowLevelUpMode()
})

// Wrapper functions for template ref assignments (refs must be set via .value in script)
const setShowAssignmentsSidePanelFalse = () => {
    showAssignmentsSidePanel.value = false
}
const setShowTrialLevelUpModalFalse = () => {
    showTrialLevelUpModal.value = false
}
const setShowWelcomeSeriesModalFalse = () => {
    showWelcomeSeriesModal.value = false
}
const setShowQuizSettingsSidePanelFalse = () => {
    showQuizSettingsSidePanel.value = false
}
const setQuizModeNull = () => {
    quizMode.value = null
}
const setShowUnfinishedQuizModalFalse = () => {
    showUnfinishedQuizModal.value = false
}
const setShowingExamDateModalTrue = () => {
    isShowingExamDateModal.value = true
}
const setShowQuizSettingsSidePanelTrue = () => {
    showQuizSettingsSidePanel.value = true
}
</script>
<style lang="scss" scoped>
.study {
    font-size: 15px;
    max-width: 1024px;
    margin: 0 auto;
    width: 100%;
    padding: 0 34px;
    box-sizing: border-box;

    @include breakpoint(black-bear) {
        padding: 0;
        display: block;
    }

    &::before {
        content: '';
        position: absolute;
        left: 0;
        height: 421px;
        width: 100%;

        @include breakpoint(brown-bear) {
            height: 330px;
        }

        @include breakpoint(black-bear) {
            display: none;
        }
    }

    &::after {
        @include breakpoint(black-bear) {
            content: '';
            position: fixed;
            left: 0;
            top: 0;
            width: 100vw;
            height: 100vh;
            background: $gray-background;
            z-index: -2;
        }
    }

    &--dark::after {
        background: $charcoal;
    }

    &--early {
        &::before {
            background-color: rgba($sunset-papaya, 0.08);
        }

        &--dark {
            &::before {
                background-color: $moonlit-ocean;
            }
        }
    }

    &--morning {
        &::before {
            background-color: rgba($manilla, 0.9);
        }

        &--dark {
            &::before {
                background-color: $moonlit-ocean;
            }
        }
    }

    &--afternoon {
        &::before {
            background-color: rgba($cream-puff, 0.9);
        }

        &--dark {
            &::before {
                background-color: $brand-black;
            }
        }
    }

    &--evening {
        &::before {
            background-color: #eef5ff;
        }

        &--dark {
            &::before {
                background-color: $evening;
            }
        }
    }

    &--midnight {
        &::before {
            background-color: $midnight-blue;
        }

        &--dark {
            &::before {
                background-color: $evening;
            }
        }
    }

    &__line {
        content: '';
        position: absolute;
        top: 186px;
        right: 0;
        width: calc(50% - 22px);
        height: 1px;
        background: rgba($brand-black, 0.16);
        z-index: 1;

        @include breakpoint(brown-bear) {
            width: calc(50% - 350px);
            top: 101px;
        }

        @include breakpoint(black-bear) {
            display: none;
        }

        &--dark {
            background: $white;
            opacity: 0.41;
        }
    }

    &__container {
        position: relative;

        &::before {
            content: '';
            position: absolute;
            height: 1px;
            background: rgba($brand-black, 0.16);
            top: 186px;
            right: 0;
            width: 25%;

            @include breakpoint(brown-bear) {
                display: none;
            }
        }
    }

    &__header {
        height: 421px;
        display: flex;
        position: relative;
        z-index: 1;

        @include breakpoint(brown-bear) {
            display: block;
            max-width: 700px;
            margin: 0 auto;
            height: 330px;
        }

        @include breakpoint(black-bear) {
            min-height: 335px;
            height: auto;
            max-width: none;
        }
    }

    &__header-right {
        padding-left: 62px;
        padding-top: 80px;
        position: relative;

        @include breakpoint(brown-bear) {
            display: none;
        }
    }

    &__header-left {
        width: 500px;

        @include breakpoint(brown-bear) {
            display: flex;
            justify-content: space-between;
            width: 100%;
        }

        @include breakpoint(black-bear) {
            width: 100%;
            display: block;
            border-bottom: 2px solid $white;
            padding-bottom: 14px;
        }

        &--early {
            @include breakpoint(black-bear) {
                background-color: rgba($cream-puff, 0.41);
            }

            &--dark {
                background-color: $moonlit-ocean;
            }
        }

        &--morning {
            @include breakpoint(black-bear) {
                background-color: $cream-puff;
            }

            &--dark {
                background-color: $moonlit-ocean;
            }
        }

        &--afternoon {
            @include breakpoint(black-bear) {
                background-color: rgba($banana-bread, 0.5);
            }

            &--dark {
                background-color: $brand-black;
            }
        }

        &--evening {
            @include breakpoint(black-bear) {
                background-color: $barely-blue;
            }

            &--dark {
                background-color: $evening;
            }
        }

        &--midnight {
            color: $white;

            @include breakpoint(black-bear) {
                background-color: $midnight-blue;
            }

            &--dark {
                background-color: $evening;
            }
        }

        &--dark {
            @include breakpoint(black-bear) {
                border-color: $charcoal;
            }
        }
    }

    &__body {
        max-width: 1012px;
        width: 100%;
        margin: 0 auto;
        padding: 30px 0 0;
        box-sizing: border-box;

        @include breakpoint(brown-bear) {
            margin-top: 0;
            max-width: 700px;
            padding: 28px 0 0;
        }

        @include breakpoint(black-bear) {
            padding: 16px 18px 0;
        }

        &--assignments {
            @include breakpoint(brown-bear) {
                padding-top: 28px;
            }

            @include breakpoint(black-bear) {
                padding-top: 16px;
            }
        }
    }

    &__exam-date-banner {
        width: 100%;
        max-width: 500px;
        box-sizing: border-box;
        margin-top: -10px;
        margin-bottom: 26px;

        @include breakpoint(brown-bear) {
            max-width: none;
            margin-top: -8px;
        }

        @include breakpoint(black-bear) {
            margin-top: 0;
        }

        &--with-assignment {
            @include breakpoint(brown-bear) {
                margin-bottom: 16px;
            }

            @include breakpoint(black-bear) {
                margin-bottom: 21px;
            }
        }
    }

    &__mobile-cta {
        margin-bottom: 20px;
        width: 100%;
    }

    &__body-top {
        display: flex;
        justify-content: space-between;
        max-width: 500px;
        width: 100%;
        margin-bottom: 16px;
        box-sizing: border-box;

        @include breakpoint(brown-bear) {
            max-width: none;
        }

        @include breakpoint(black-bear) {
            padding: 0 8px;
            margin: 0 0 14px;
        }

        button {
            padding: 0;
        }
    }

    &__body-title {
        font-size: 15px;
        line-height: 18px;
        font-weight: 600;
        margin: 0;

        &--dark {
            color: $white;
        }
    }

    &__modes,
    &__assignment-mode {
        margin: -6.5px;
        max-width: 513px;
        width: 100%;
        display: flex;
        flex-wrap: wrap;
        margin-bottom: 60px;

        @include breakpoint(brown-bear) {
            max-width: none;
            margin: -8px;
            width: calc(100% + 16px);
        }

        @include breakpoint(black-bear) {
            margin: -5px 0;
            width: 100%;
            display: block;
            padding-bottom: 50px;
        }
    }

    &__assignment-mode {
        @include breakpoint(brown-bear) {
            padding-bottom: 28px;
        }
    }

    &__mode {
        margin: 6.5px;

        @include breakpoint(brown-bear) {
            margin: 8px;
        }

        @include breakpoint(black-bear) {
            margin: 0 0 10px;
        }
    }

    &__mode-qotd {
        display: none;

        @include breakpoint(brown-bear) {
            display: flex;
        }
    }

    &__blob {
        position: absolute;
        left: 50%;
        margin-left: -122px;
        width: 244px;
        height: 46px;
    }

    &__title {
        max-width: 1012px;
        width: 100%;
        padding: 80px 0 0;
        margin: 0 auto 29px;
        box-sizing: border-box;

        @include breakpoint(brown-bear) {
            max-width: 230px;
            margin: 0;
        }

        @include breakpoint(black-bear) {
            padding: 66px 30px 0;
            margin-bottom: 30px;
            max-width: none;

            &--ten-day-cta {
                margin-bottom: 60px;
            }
        }

        &--dark {
            color: $white;
        }

        h1 {
            margin: 0;
            font-size: 26px;
            font-weight: 600;
            line-height: 34px;
            margin-bottom: 5px;
        }
    }

    &__subhead {
        position: relative;
        height: 16px;

        @include breakpoint(black-bear) {
            height: auto;
        }
    }

    &__greeting {
        font-size: 15px;
        line-height: 18px;
        letter-spacing: 0.2px;

        :deep(img) {
            height: 16px;
            margin-left: 3px;
            display: inline;
            margin-bottom: -4px;
        }
    }

    &__share-cta,
    &__exam-date-greeting {
        display: flex;
        width: 780px;

        @include breakpoint(black-bear) {
            width: 100%;
            max-width: 343px;
            margin-bottom: 32px;
        }
    }

    &__share-cta-message,
    &__exam-date-greeting-message {
        color: $pickled-bluewood;
        font-size: 15px;
        font-weight: 500;
        line-height: 19px;
        text-align: left;
        display: flex;
        align-items: center;

        &--dark {
            color: $white;
        }

        @include breakpoint(brown-bear) {
            display: block;
            width: 225px;

            &.study__exam-date-greeting-message {
                width: 160px;
            }
        }

        @include breakpoint(black-bear) {
            display: block;
            width: 100%;
            line-height: 22px;
            padding: 0;

            &.study__exam-date-greeting-message {
                width: 100%;
            }
        }
    }

    &__share-cta-btn,
    &__exam-date-greeting-btn {
        margin-left: 4px;

        @include breakpoint(brown-bear) {
            margin-left: 0px;
        }
    }
}
</style>
